import { DOCUMENT } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import { Directive, ElementRef, Inject, Input, SecurityContext, output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import type { Subscription } from 'rxjs';
import { fromEvent as observableFromEvent, merge as observableMerge } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[rtHighlight]',
  host: {
    '[attr.contenteditable]': '!disabled',
    '[style.whiteSpace]': '"pre"',
  },
  standalone: true,
})
export class SearchPageQueryInputHighlightDirective {
  @Input('rtHighlight') public value: string;
  @Input('rtHighlightExpression') public keywordExpression: RegExp;
  @Input('rtHighlightEnableWrap') public keywordWrapEnabled: boolean = true;
  @Input('rtHighlightDisabled') public disabled: boolean = false;

  public valueChange = output<string>();

  private rawTextValue: string;
  private subscription: Subscription | null;

  constructor(
    private elRef: ElementRef,
    private sanitizer: DomSanitizer,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  public ngOnInit(): void {
    const element = this.elRef.nativeElement;
    this.subscription = observableFromEvent(element, 'focus')
      .pipe(
        switchMap(() =>
          observableMerge(
            observableFromEvent(element, 'keydown'),
            observableFromEvent(element, 'input'),
            observableFromEvent(element, 'paste'),
          ).pipe(takeUntil(observableFromEvent(element, 'blur'))),
        ),
      )
      .subscribe((event: any) => {
        // eslint-disable-next-line default-case
        switch (event.type) {
          case 'input':
            this.handleInput(event);
            break;

          case 'keydown':
            this.handleKeyDown(event);
            break;

          case 'paste':
            this.handlePaste(event);
            break;
        }
      });
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscription = null;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { value, keywordExpression, keywordWrapEnabled } = changes;

    if (value) {
      if (value.currentValue) {
        // normalize characters
        const current = value.currentValue.replace(/(\u00A0|\/s)/g, ' ');
        const previous = this.rawTextValue ? this.rawTextValue.replace(/(\u00A0|\/s)/g, ' ') : '';

        if (current !== previous || !value.previousValue) {
          this.render(this.format(this.sanitizer.sanitize(SecurityContext.HTML, current)!));
          this.position(this.elRef.nativeElement, (current || '').length);
          return;
        }
      } else {
        this.render('');
      }
    }

    if (
      keywordExpression &&
      !keywordExpression.firstChange &&
      keywordExpression.currentValue !== keywordExpression.previousValue
    ) {
      this.render(this.format(this.elRef.nativeElement.innerText));
      return;
    }

    if (
      keywordWrapEnabled &&
      keywordWrapEnabled.currentValue !== keywordWrapEnabled.previousValue
    ) {
      const { innerText } = this.elRef.nativeElement;
      this.render(
        this.format(innerText.replace(/(\r\n\t|\n|\r\t)/gm, '').replace(/(\u00A0|\/s)/g, ' ')),
      );
    }
  }

  public handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    this.doc.execCommand(
      'insertHTML',
      false,
      this.format(
        this.sanitizer.sanitize(SecurityContext.HTML, event.clipboardData!.getData('text/plain'))!,
      ),
    );
  }

  public handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Backspace' && this.elRef.nativeElement.innerText.length === 0) {
      event.preventDefault();
    }

    if (event.key === 'Tab' || event.key === 'Enter') {
      event.preventDefault();
    }
  }

  public handleInput(event: Event): void {
    const textContent = (<Document>event.target).textContent!;
    this.highlight(textContent);
    this.valueChange.emit(textContent.replace(/\u00A0|\s/g, ' '));
  }

  private highlight(text: string): void {
    const pos = this.position(this.elRef.nativeElement);
    this.render(this.format(text));
    this.position(this.elRef.nativeElement, pos);
  }

  private format(text: string): string {
    const whitespacePreservedText = text.replace(/\s/g, '&nbsp;');

    this.rawTextValue = text;

    if (this.keywordWrapEnabled) {
      return `${whitespacePreservedText.replace(this.keywordExpression, this.wrapKeyword)}`;
    } else {
      return whitespacePreservedText;
    }
  }

  private wrapKeyword(keyword: string): string {
    return `<span class="keyword">${keyword}</span>`;
  }

  private render(text: string): void {
    // eslint-disable-next-line no-restricted-properties
    this.elRef.nativeElement.innerHTML = text;
  }

  private position(el: Element, at?: number): number | undefined {
    if (1 === arguments.length) {
      const range = window.getSelection()!.getRangeAt(0);
      const clone = range.cloneRange();
      clone.selectNodeContents(el);
      clone.setEnd(range.endContainer, range.endOffset);
      return clone.toString().length;
    }

    let length = 0;
    let abort: boolean;

    this.visit(el, function (node: Node): boolean | undefined {
      if (3 !== node.nodeType) return false;
      length += node.textContent!.length;
      if (length >= at!) {
        if (abort) return false;
        abort = true;
        const sel = document.getSelection()!;
        const range = document.createRange();
        const sub = length - node.textContent!.length;
        range.setStart(node, at! - sub);
        range.setEnd(node, at! - sub);
        sel.removeAllRanges();
        sel.addRange(range);
        return true;
      }

      return;
    });

    return;
  }

  private visit(node: Element | Node, fn: Function): void {
    const nodes = node.childNodes;
    for (let i = 0; i < nodes.length; ++i) {
      if (fn(nodes[i])) break;
      this.visit(nodes[i], fn);
    }
  }
}
